import React, { useEffect, useMemo, useState } from 'react';
import { X } from '@phosphor-icons/react';
import CoordinatesPickerField from './CoordinatesPickerField.jsx';
import DateTimePickerField from './DateTimePickerField.jsx';

const getValueByPath = (obj, path) => {
	if (!path) return undefined;
	return path.split('.').reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
};

const setValueByPath = (obj, path, value) => {
	const keys = path.split('.');
	const result = { ...obj };
	let current = result;

	for (let i = 0; i < keys.length - 1; i += 1) {
		const key = keys[i];
		current[key] = current[key] && typeof current[key] === 'object' ? { ...current[key] } : {};
		current = current[key];
	}

	current[keys[keys.length - 1]] = value;
	return result;
};

const getDefaultValueByType = (type) => {
	if (type === 'checkbox') return false;
	return '';
};

const normalizeInitialValues = (fields, initialValues = {}) => {
	let values = { ...initialValues };

	fields.forEach((field) => {
		if (!field?.name) return;
		const currentValue = getValueByPath(values, field.name);
		if (currentValue === undefined) {
			values = setValueByPath(values, field.name, field.defaultValue ?? getDefaultValueByType(field.type));
		}
	});

	return values;
};

const validateField = (field, value, values, mode) => {
	const isRequired = typeof field.required === 'function' ? field.required(values, mode) : field.required;

	if (isRequired) {
		const empty =
			value === undefined ||
			value === null ||
			(typeof value === 'string' && value.trim() === '') ||
			(Array.isArray(value) && value.length === 0);

		if (empty) {
			return field.requiredMessage || 'Este campo es obligatorio';
		}
	}

	if (value === undefined || value === null || value === '') {
		return null;
	}

	if (typeof value === 'string') {
		if (field.minLength && value.length < field.minLength) {
			return field.minLengthMessage || `Debe tener al menos ${field.minLength} caracteres`;
		}
		if (field.maxLength && value.length > field.maxLength) {
			return field.maxLengthMessage || `Debe tener como maximo ${field.maxLength} caracteres`;
		}
	}

	if (field.type === 'number') {
		const numericValue = Number(value);
		if (Number.isNaN(numericValue)) {
			return field.invalidNumberMessage || 'Debe ser un numero valido';
		}
		if (field.min !== undefined && numericValue < field.min) {
			return field.minMessage || `Debe ser mayor o igual a ${field.min}`;
		}
		if (field.max !== undefined && numericValue > field.max) {
			return field.maxMessage || `Debe ser menor o igual a ${field.max}`;
		}
	}

	if (field.pattern) {
		const regex = field.pattern instanceof RegExp ? field.pattern : new RegExp(field.pattern);
		if (!regex.test(String(value))) {
			return field.patternMessage || 'Formato invalido';
		}
	}

	if (typeof field.validate === 'function') {
		const customError = field.validate(value, values, mode);
		if (customError) return customError;
	}

	return null;
};

const renderInputByType = ({ field, value, onChange, onBlur, disabled }) => {
	const commonClassName =
		'w-full h-12 px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line';

	if (field.type === 'textarea') {
		return (
			<textarea
				name={field.name}
				value={value ?? ''}
				onChange={(event) => onChange(event.target.value)}
				onBlur={onBlur}
				placeholder={field.placeholder || ''}
				disabled={disabled}
				rows={field.rows || 3}
				className="w-full px-3 py-2 rounded-md bg-surface-2 text-content placeholder-content-muted focus:outline-none focus:ring-2 focus:ring-amber text-sm border border-line min-h-[80px] resize-none"
			/>
		);
	}

	if (field.type === 'select') {
		return (
			<select
				name={field.name}
				value={value ?? ''}
				onChange={(event) => onChange(event.target.value)}
				onBlur={onBlur}
				disabled={disabled}
				className={commonClassName}
			>
				{field.placeholder && <option value="">{field.placeholder}</option>}
				{(field.options || []).map((option) => {
					const optionValue = typeof option === 'object' ? option.value : option;
					const optionLabel = typeof option === 'object' ? option.label : option;
					return (
						<option key={String(optionValue)} value={optionValue}>
							{optionLabel}
						</option>
					);
				})}
			</select>
		);
	}

	if (field.type === 'datetime') {
		return (
			<DateTimePickerField
				value={value ?? ''}
				onChange={onChange}
				disabled={disabled}
				mode="datetime"
				placeholder={field.placeholder || 'dd/mm/aaaa --:--'}
				buttonClassName="h-12 rounded-md bg-surface-2"
			/>
		);
	}

	if (field.type === 'coordinates') {
		return (
			<CoordinatesPickerField
				value={value}
				onChange={onChange}
				onBlur={onBlur}
				disabled={disabled}
				normalizeCoordinates={field.normalizeCoordinates}
				placeholder={field.placeholder}
				markerId={field.markerId || `${field.name}-location`}
			/>
		);
	}

	if (field.type === 'toggle') {
		return (
			<label className="flex min-h-12 items-center gap-3 text-sm font-medium text-content">
				<span>{field.label}</span>
				<button
					type="button"
					role="switch"
					aria-checked={Boolean(value)}
					onClick={() => onChange(!value)}
					disabled={disabled}
					className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-amber focus:ring-offset-2 focus:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-60 ${
						value ? 'bg-amber' : 'bg-line'
					}`}
				>
					<span
						className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
							value ? 'translate-x-6' : 'translate-x-1'
						}`}
					/>
				</button>
			</label>
		);
	}

	if (field.type === 'checkbox') {
		return (
			<label className="flex items-center gap-2 h-12 text-sm text-content">
				<input
					name={field.name}
					type="checkbox"
					checked={Boolean(value)}
					onChange={(event) => onChange(event.target.checked)}
					onBlur={onBlur}
					disabled={disabled}
					className="h-4 w-4 rounded border-line bg-surface-2 text-amber focus:ring-amber"
				/>
				{field.checkboxLabel || field.label}
			</label>
		);
	}

	return (
		<input
			name={field.name}
			type={field.type || 'text'}
			value={value ?? ''}
			onChange={(event) => onChange(event.target.value)}
			onBlur={onBlur}
			placeholder={field.placeholder || ''}
			disabled={disabled}
			min={field.min}
			max={field.max}
			step={field.step}
			inputMode={field.inputMode}
			maxLength={field.maxLength}
			className={commonClassName}
		/>
	);
};

const ModalCreationEditing = ({
	isOpen,
	mode = 'create',
	title,
	icon,
	subtitle,
	fields = [],
	sections = [],
	initialValues = {},
	initialValuesKey,
	submitText,
	cancelText = 'Cancelar',
	isSubmitting = false,
	onSubmit,
	onClose,
	validate,
	onValuesChange,
	maxWidthClassName = 'max-w-4xl',
	columns = 3,
	notification,
	actions,
	footer,
	children,
	contentClassName = '',
	onModeChange,
	actionsPlacement,
	modeActionPlacement,
	submitActionPlacement,
	viewModeToggleText = 'Cambiar a Edición',
	editModeToggleText = 'Cambiar a Visualización',
}) => {
	const configuredFields = useMemo(
		() => (sections.length > 0 ? sections.flatMap((section) => section.fields || []) : fields),
		[fields, sections]
	);
	const [values, setValues] = useState(() => normalizeInitialValues(configuredFields, initialValues));
	const [touched, setTouched] = useState({});
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (!isOpen) return;
		setValues(normalizeInitialValues(configuredFields, initialValues));
		setTouched({});
		setErrors({});
	}, [isOpen, initialValuesKey]);

	const visibleFields = useMemo(
		() => configuredFields.filter((field) => {
			const hidden = typeof field.hidden === 'function' ? field.hidden(values, mode) : field.hidden;
			return !hidden;
		}),
		[configuredFields, values, mode]
	);

	const runValidation = (draftValues) => {
		const nextErrors = {};

		visibleFields.forEach((field) => {
			if (!field?.name) return;
			const value = getValueByPath(draftValues, field.name);
			const error = validateField(field, value, draftValues, mode);
			if (error) nextErrors[field.name] = error;
		});

		if (typeof validate === 'function') {
			const extraErrors = validate(draftValues, mode) || {};
			Object.assign(nextErrors, extraErrors);
		}

		return nextErrors;
	};

	const handleValueChange = (field, nextValue) => {
		const transformedValue = typeof field.transformValue === 'function'
			? field.transformValue(nextValue, values, mode)
			: nextValue;
		let nextValues = setValueByPath(values, field.name, transformedValue);

		if (typeof field.onValueChange === 'function') {
			nextValues = field.onValueChange(transformedValue, nextValues, mode) || nextValues;
		}

		setValues(nextValues);

		if (onValuesChange) {
			onValuesChange(nextValues);
		}

		if (touched[field.name]) {
			const nextErrors = runValidation(nextValues);
			setErrors(nextErrors);
		}
	};

	const handleBlur = (fieldName) => {
		const nextTouched = { ...touched, [fieldName]: true };
		setTouched(nextTouched);
		const nextErrors = runValidation(values);
		setErrors(nextErrors);
	};

	const isViewMode = mode === 'view';
	const canToggleMode = typeof onModeChange === 'function' && (isViewMode || mode === 'edit');

	const submitValues = () => {
		if (isViewMode || isSubmitting) return;

		const allTouched = visibleFields.reduce((acc, field) => {
			if (field?.name) acc[field.name] = true;
			return acc;
		}, {});

		setTouched(allTouched);
		const nextErrors = runValidation(values);
		setErrors(nextErrors);

		if (Object.keys(nextErrors).length > 0) return;
		if (onSubmit) onSubmit(values);
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		submitValues();
	};

	if (!isOpen) return null;

	const lgColsClassName = { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3' }[columns] || 'lg:grid-cols-3';
	const computedTitle = title || (mode === 'edit' ? 'Editar' : mode === 'view' ? 'Ver' : 'Crear');
	const computedSubmitText = submitText || (mode === 'edit' ? 'Guardar cambios' : 'Crear');
	const resolvedModeActionPlacement = modeActionPlacement || actionsPlacement || 'header';
	const resolvedSubmitActionPlacement = submitActionPlacement || actionsPlacement || 'footer';
	const modeAction = canToggleMode ? (
		<button
			type="button"
			onClick={() => onModeChange(isViewMode ? 'edit' : 'view')}
			className="px-4 py-2 rounded-lg bg-surface-2 text-content hover:bg-line text-sm font-medium transition-colors"
		>
			{isViewMode ? viewModeToggleText : editModeToggleText}
		</button>
	) : null;
	const submitAction = !isViewMode ? (
		<button
			type="button"
			onClick={submitValues}
			disabled={isSubmitting}
			className={`px-6 py-3 rounded-lg font-semibold transition-colors ${
				isSubmitting ? 'cursor-not-allowed bg-surface-2 text-content-muted' : 'bg-amber text-on-amber hover:bg-amber-hover'
			}`}
		>
			{isSubmitting ? 'Procesando...' : computedSubmitText}
		</button>
	) : null;
	const stickySection = !isViewMode
		? resolvedSubmitActionPlacement
		: modeAction
			? resolvedModeActionPlacement
			: 'footer';
	const stickyHeader = stickySection === 'header';
	const stickyFooter = stickySection === 'footer';
	const renderConfiguredField = (field) => {
		const fieldValue = getValueByPath(values, field.name);
		const fieldError = touched[field.name] ? errors[field.name] : null;
		const disabled = isViewMode || (typeof field.disabled === 'function' ? field.disabled(values, mode) : field.disabled);
		const fieldClassName = typeof field.className === 'function' ? field.className(values, mode) : field.className;
		const colSpanClassName = field.colSpan ? 'col-span-full' : '';

		return (
			<div key={field.name} data-tour={field.tour} className={`${colSpanClassName} ${fieldClassName || ''}`.trim()}>
				{field.type !== 'checkbox' && field.type !== 'toggle' && (
					<label className="block text-sm font-medium text-white mb-2">
						{field.label}
						{(typeof field.required === 'function' ? field.required(values, mode) : field.required) && (
							<span className="text-red-500 ml-1">*</span>
						)}
					</label>
				)}

				{field.type === 'custom' && typeof field.render === 'function'
					? field.render({
							values,
							value: fieldValue,
							onChange: (value) => handleValueChange(field, value),
							onBlur: () => handleBlur(field.name),
							mode,
							disabled,
						})
					: renderInputByType({
							field,
							value: fieldValue,
							onChange: (value) => handleValueChange(field, value),
							onBlur: () => handleBlur(field.name),
							disabled,
						})}

				{fieldError && <p className="text-xs sm:text-sm font-medium text-red-600 mt-1">{fieldError}</p>}
				{!fieldError && field.helperText && <p className="text-xs text-content-muted mt-1">{field.helperText}</p>}
			</div>
		);
	};

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-9998 p-4 !mt-0">
			<div data-tour-scroll className={`bg-surface rounded-lg shadow-xl border border-line w-full max-h-[90vh] overflow-y-auto ${maxWidthClassName}`.trim()}>
				<div className={`${stickyHeader ? 'sticky top-0' : ''} bg-surface z-30 flex items-center justify-between p-6 border-b border-line`.trim()}>
					<div className="flex items-start gap-3">
						{icon && <span className="text-content-muted mt-1 shrink-0">{icon}</span>}
						<div>
							<h2 className="text-2xl font-bold text-white">{computedTitle}</h2>
							{subtitle && <p className="text-sm text-content">{subtitle}</p>}
						</div>
					</div>
					<div className="flex items-center gap-3">
						{resolvedModeActionPlacement === 'header' && modeAction}
						{resolvedSubmitActionPlacement === 'header' && submitAction}
						<button onClick={onClose} className="text-content-muted hover:text-content text-2xl" type="button">
							<X size={20} />
						</button>
					</div>
				</div>

				<form className={`p-6 ${contentClassName}`.trim()} onSubmit={handleSubmit}>
					{notification?.show && (
						<div
							className={`notification-message mb-4 p-4 rounded-lg ${
								notification.type === 'success'
									? 'bg-green-900/20 border border-green-600 text-green-400'
									: 'bg-red-900/20 border border-red-600 text-red-400'
							}`}
						>
							{notification.message}
						</div>
					)}

					{children || (sections.length > 0 ? (
						<div>
							{sections.map((section) => {
								const sectionHidden = typeof section.hidden === 'function' ? section.hidden(values, mode) : section.hidden;
								if (sectionHidden) return null;

								const sectionFields = (section.fields || []).filter(
									(field) => visibleFields.includes(field) || field.reserveSpaceWhenHidden
								);
								if (sectionFields.length === 0) return null;

								return (
									<section key={section.id || section.title} data-tour={section.tour} className={section.className || ''}>
										{section.title && <h3 className="mb-3 text-base font-semibold text-white">{section.title}</h3>}
										<div className={section.gridClassName || `grid grid-cols-1 sm:grid-cols-2 ${lgColsClassName} gap-4`}>
											{sectionFields.map((field) => (
												visibleFields.includes(field)
													? renderConfiguredField(field)
													: <div key={`${field.name}-reserved-space`} aria-hidden="true" />
											))}
										</div>
									</section>
								);
							})}
						</div>
					) : (
						<div className={`grid grid-cols-1 sm:grid-cols-2 ${lgColsClassName} gap-4`}>
							{visibleFields.map(renderConfiguredField)}
						</div>
					))}

					{actions}

					{footer || (
						<div
							className={`${
								stickyFooter
									? 'sticky bottom-0 z-30 -mx-6 -mb-6 mt-6 flex justify-end gap-4 bg-surface px-6 py-4 shadow-[0_-8px_16px_rgba(0,0,0,0.16)]'
									: 'mt-8 flex justify-end gap-4 pt-2'
							}`}
						>
							<button
								type="button"
								onClick={onClose}
								className="px-6 py-3 bg-surface-2 text-content rounded-lg hover:bg-line transition-colors"
							>
								{cancelText}
							</button>
							{resolvedModeActionPlacement === 'footer' && modeAction}
							{resolvedSubmitActionPlacement === 'footer' && submitAction}
						</div>
					)}
				</form>
			</div>
		</div>
	);
};

export default ModalCreationEditing;
